import moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'

import Button from '../../../../App/Components/Button/Button'
import Message from '../../../../App/Components/Message/Message'
import Spinner from '../../../../App/Components/Spinner/Spinner'
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../App/Hooks/reduxHooks'
import { IActivity } from '../../interface/IActivity'
import { IWaitingCustomer } from '../../interface/IWaitingCustomer'
import {
  getWaitingCustomers,
  updateWaitingCustomers,
} from '../../store/Actions'
import { DateRangeBar } from '../DateRangeBar/DateRangeBar'
import { IProps } from '../DateRangeBar/IProps'
import WaitingListCard from '../WaitingListCard/WaitingListCard'

import style from './style.scss'

const Overview = ({
  isRangePickerVisible,
  setIsRangePickerVisible,
  startDateHook,
  endDateHook,
  setStartDateHook,
  setEndDateHook,
}: IProps) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const history = useHistory()

  const [waitingCustomersOrdered, setWaitingCustomersOrdered] = useState<
    IWaitingCustomer[]
  >([])

  const { startDate, endDate } = useParams<{
    startDate: string | undefined
    endDate: string | undefined
  }>()

  const { loading, waitingCustomers, menuList, activityList } = useAppSelector(
    state => {
      if (!state.calendar.waitingList) {
        return {
          loading: false,
          waitingCustomers: undefined,
        }
      }
      return {
        loading: state.calendar.waitingList.isLoading,
        waitingCustomers: state.calendar.waitingList.waitingCustomers,
        menuList: state.calendar.waitingList.menuList,
        activityList: state.calendar.waitingList.activityList,
      }
    },
  )

  useEffect(() => {
    if (typeof waitingCustomers !== 'undefined') {
      setWaitingCustomersOrdered(
        waitingCustomers.length > 0 ? waitingCustomers : [],
      )
    }
  }, [waitingCustomers])

  useEffect(() => {
    if (
      typeof startDateHook === 'undefined' &&
      typeof endDate !== 'undefined'
    ) {
      setStartDateHook(moment(startDate))
      setEndDateHook(moment(endDate))
    }

    dispatch(getWaitingCustomers(startDateHook, endDateHook))
  }, [startDateHook, endDate, endDateHook, dispatch])

  const extractedActivities = useMemo(() => {
    return activityList?.flatMap(data => data.activities)
  }, [activityList, menuList])

  useEffect(() => {
    if (waitingCustomers && extractedActivities) {
      const newUpdatedCustomer = waitingCustomers.map(customer => {
        if (customer.activities) {
          const matchingActivities = customer.activities
            .map(activity =>
              extractedActivities.find(
                extractedActivity => extractedActivity.uuid === activity.uuid,
              ),
            )
            .filter((activity): activity is IActivity => Boolean(activity))
          return { ...customer, matchingActivities }
        } else {
          return customer
        }
      })
      setWaitingCustomersOrdered(newUpdatedCustomer)
    }
  }, [waitingCustomers, extractedActivities])

  const onDragEndEvent = (result: DropResult) => {
    if (!result.destination) {
      return
    }

    const newWaitingCustomersOrdered = reorder(
      waitingCustomersOrdered,
      result.source.index,
      result.destination.index,
    )

    if (result.destination.index !== result.source.index) {
      setWaitingCustomersOrdered(newWaitingCustomersOrdered)
      dispatch(updateWaitingCustomers(newWaitingCustomersOrdered))
    }
  }

  const reorder = (
    list: IWaitingCustomer[],
    startIndex: number,
    endIndex: number,
  ) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)

    return result
  }

  return (
    <>
      <DateRangeBar
        isRangePickerVisible={isRangePickerVisible}
        setIsRangePickerVisible={setIsRangePickerVisible}
        startDateHook={startDateHook}
        endDateHook={endDateHook}
        setStartDateHook={setStartDateHook}
        setEndDateHook={setEndDateHook}
      />
      <div className={style.container}>
        <Button
          type={'button'}
          onClick={() => history.push('/calendar/waiting-list/create')}>
          {t('Add customer')}
        </Button>
        {loading || typeof waitingCustomersOrdered === 'undefined' ? (
          <Spinner />
        ) : waitingCustomersOrdered.length === 0 ? (
          <div className={style.message}>
            <Message>{t('No waiting customers found.')}</Message>
          </div>
        ) : (
          <div className={style.list}>
            <DragDropContext onDragEnd={onDragEndEvent}>
              <Droppable droppableId="droppable">
                {dropProvided => (
                  <div
                    {...dropProvided.droppableProps}
                    ref={dropProvided.innerRef}>
                    {waitingCustomersOrdered.map(
                      (waitingCustomer: IWaitingCustomer, index: number) => (
                        <Draggable
                          key={waitingCustomer.id}
                          draggableId={String(waitingCustomer.id)}
                          index={index}>
                          {dragProvided => (
                            <div
                              ref={dragProvided.innerRef}
                              {...dragProvided.draggableProps}
                              {...dragProvided.dragHandleProps}>
                              <WaitingListCard
                                waitingCustomer={waitingCustomer}
                                key={waitingCustomer.id}
                              />
                            </div>
                          )}
                        </Draggable>
                      ),
                    )}
                    {dropProvided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
        )}
      </div>
    </>
  )
}

export default Overview
